import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import { Table, Button, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Search } from "react-bootstrap-icons";

import { CellActions } from "../../../common/cell-actions/cell-actions";
import { Loading } from "../../../common/loading/loading";
import { formatDate } from "../../../../utils/date-utils/date-utils";
import { useDownloadPdf } from "../../../../api/service-worksheet-api";
import { useUiFeedback } from "../../../../context/ui-feedback/ui-feedback";
import { createErrorFeedback } from "../../../../context/ui-feedback/action";
import { SimpleModal } from "../../../common/modal/simple-modal";
import { useDetailHistory } from "../../../../api/service-history-api";
import { ServiceHistoryDetail } from "./service-history-detail";

export const ServiceHistoryTable = ({ rowNumberInit, histories, hasError }) => {
  const intl = useIntl();
  const navigate = useNavigate();

  const [selectedSw, setSelectedSw] = useState(null);

  const { uiFeedbackDispatch } = useUiFeedback();

  const downloadPdf = useDownloadPdf();
  const loadDetailById = useDetailHistory();

  useEffect(() => {
    const loadDetail = async (swId) => {
      try {
        await loadDetailById(swId, (data) => {
          if (data instanceof Error) {
            throw data;
          } else {
            setSelectedSw({
              ...selectedSw,
              data: data,
            });
          }
        });
      } catch (ex) {
        if (ex.response?.status === 401 || ex.response?.status === 403) {
          navigate("/");
        } else {
          setSelectedSw({
            ...selectedSw,
            error: intl.formatMessage({
              id: "data.servicehistory.exception.getServiceHistory",
            }),
          });
        }
      }
    };
    const checkNotEmpy = (items) => {
      return items.filter((item) => item === null).length === 0;
    };

    if (
      checkNotEmpy([intl, navigate]) &&
      selectedSw?.swId &&
      !selectedSw?.data
    ) {
      loadDetail(selectedSw.swId);
    }
  }, [loadDetailById, selectedSw, intl, navigate]);

  const download = async (serviceWorksheetId, pdfName, shortVersion) => {
    try {
      await downloadPdf(serviceWorksheetId, pdfName, shortVersion, (data) => {
        if (data instanceof Error) {
          throw data;
        }
      });
    } catch (ex) {
      if (ex.response?.status === 401 || ex.response?.status === 403) {
        navigate("/");
      } else {
        createErrorFeedback(
          uiFeedbackDispatch,
          intl.formatMessage({
            id: "page.editserviceworksheet.exception.downloadpdf.title",
          }),
          intl.formatMessage({
            id: "page.editserviceworksheet.exception.downloadpdf.description",
          }),
        );
      }
    }
  };

  const TableBody = ({ histories, rowNumberInit }) => {
    if (histories.length === 0) {
      return (
        <tr>
          <td colSpan={8}>
            <span className="text-danger">
              <b>
                <FormattedMessage id="data.servicehistory.warning.notfoundservicehistory" />
              </b>
            </span>
          </td>
        </tr>
      );
    }

    const getFormatedSWDate = (period) => {
      return `${formatDate(new Date(period.startLong), "YYYY.MM.DD. hh:mm")} - ${formatDate(new Date(period.endLong), "YYYY.MM.DD. hh:mm")}`;
    };

    return histories.map((history, index) => {
      const buttons = [];
      buttons.push({
        variant: "secondary",
        title: intl.formatMessage({
          id: "data.servicehistory.table.details",
        }),
        icon: <Search />,
        action: () => {
          setSelectedSw({
            swId: history.pdf.swId,
            no: history.serviceWorksheetNo,
          });
        },
      });

      return (
        <tr key={history.serviceWorksheetNo}>
          <td className="col-rownumber">{rowNumberInit + index + 1}.</td>
          <td className="col-client">{history.clientName}</td>
          <td className="col-machinedata">
            {history.machineNumber ? (
              <>
                <div>
                  <b>{history.machineNumber}</b>
                </div>
                <div>{history.machineType}</div>
                <div>
                  <i>({history.machineOperatingHours})</i>
                </div>
              </>
            ) : null}
          </td>
          <td className="col-templatename">
            <FormattedMessage
              id="data.servicehistory.table.template.value"
              values={{
                name: history.template.name,
                version: history.template.version,
              }}
            />
          </td>
          <td className="col-serviceworksheetno">
            <div className="text-center">
              <Button
                variant="link"
                onClick={() => {
                  download(history.pdf.swId, history.pdf.name, false);
                }}
                className="p-0"
              >
                {history.serviceWorksheetNo}
              </Button>
            </div>
            {history.shortPdf ? (
              <div className="text-center">
                (
                <Button
                  variant="link"
                  onClick={() => {
                    download(
                      history.shortPdf.swId,
                      history.shortPdf.name,
                      true,
                    );
                  }}
                  className="p-0"
                >
                  <FormattedMessage id="data.servicehistory.detail.shortpdf" />
                </Button>
                )
              </div>
            ) : null}
          </td>
          <td className="col-salesorderno">{history.salesOrderNo}</td>
          <td className="col-date">{getFormatedSWDate(history.date)}</td>
          <td className="col-actions">
            <CellActions buttons={buttons} />
          </td>
        </tr>
      );
    });
  };

  const LoadingBody = ({ hasError }) => (
    <tr>
      <td colSpan={8}>
        {hasError ? (
          <span className="text-danger">
            <FormattedMessage id="data.servicehistory.exception.listServiceHistory" />
          </span>
        ) : (
          <Loading />
        )}
      </td>
    </tr>
  );

  return (
    <div className="servicewhistory-table-component table-responsive">
      <Table striped bordered>
        <thead>
          <tr>
            <th className="col-rownumber">#</th>
            <th className="col-client">
              <FormattedMessage id="data.servicehistory.table.client" />
            </th>
            <th className="col-machinedata">
              <FormattedMessage id="data.servicehistory.table.machinedata" />
            </th>
            <td className="col-templatename">
              <FormattedMessage id="data.servicehistory.table.template.label" />
            </td>
            <th className="col-serviceworksheetno">
              <FormattedMessage id="data.servicehistory.table.serviceworksheetno" />
            </th>
            <th className="col-salesorderno">
              <FormattedMessage id="data.servicehistory.table.salesorderno" />
            </th>
            <th className="col-date">
              <FormattedMessage id="data.servicehistory.table.date" />
            </th>
            <th className="col-actions"></th>
          </tr>
        </thead>
        <tbody>
          {histories ? (
            <TableBody histories={histories} rowNumberInit={rowNumberInit} />
          ) : (
            <LoadingBody hasError={hasError} />
          )}
        </tbody>
      </Table>
      {selectedSw ? (
        <SimpleModal
          title={selectedSw.no}
          handleClose={() => {
            setSelectedSw(null);
          }}
        >
          {selectedSw.error ? (
            <Alert variant="danger">{selectedSw.error}</Alert>
          ) : selectedSw.data ? (
            <ServiceHistoryDetail detail={selectedSw.data} />
          ) : (
            <Loading />
          )}
        </SimpleModal>
      ) : null}
    </div>
  );
};

ServiceHistoryTable.propTypes = {
  rowNumberInit: PropTypes.number.isRequired,
  histories: PropTypes.array,
  hasError: PropTypes.bool.isRequired,
};
